import { useState, useRef, useCallback } from 'react';
import { Button } from './button';
import { Card } from './card';
import { Label } from './label';
import { Upload, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageUploadProps {
  value?: string; // 当前图片URL（用于编辑模式）
  onChange: (file: File | null) => void; // 文件选择回调
  onError?: (error: string) => void;
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  placeholder?: string;
  className?: string;
  label?: string;
  required?: boolean;
  previewUrl?: string; // 新选择的图片预览URL
  originalFilename?: string; // 原始文件名（用于编辑模式验证扩展名）
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onError,
  disabled = false,
  accept = 'image/*',
  maxSize = 10,
  placeholder = '点击或拖拽图片到此处上传',
  className,
  label,
  required = false,
  previewUrl,
  originalFilename
}) => {
  const [loading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    if (disabled || loading) return; // 防止重复处理

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      onError?.('只能上传图片文件');
      return;
    }

    // 验证文件大小
    if (file.size > maxSize * 1024 * 1024) {
      onError?.(`文件大小不能超过 ${maxSize}MB`);
      return;
    }

    // 编辑模式下验证文件扩展名
    if (originalFilename && value) {
      const originalExt = originalFilename.split('.').pop()?.toLowerCase();
      const newExt = file.name.split('.').pop()?.toLowerCase();
      
      if (originalExt && newExt && originalExt !== newExt) {
        onError?.('上传图片后缀与原图不同，请上传相同后缀图片');
        return;
      }
    }

    // 通知父组件选择了文件
    onChange(file);
  }, [disabled, loading, maxSize, onChange, onError, originalFilename, value]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // 重置input以允许重复选择同一文件
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  // 显示的图片URL：优先显示新预览，否则显示当前图片
  const displayUrl = previewUrl || value;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      
      <Card
        className={cn(
          'relative border-2 border-dashed transition-colors cursor-pointer',
          dragOver && 'border-blue-500 bg-blue-50',
          disabled && 'opacity-50 cursor-not-allowed',
          !displayUrl && 'border-gray-300 hover:border-gray-400',
          displayUrl && 'border-gray-200'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {displayUrl ? (
          <div className="relative group">
            <img
              src={displayUrl}
              alt="图片预览"
              className="w-full h-48 object-cover rounded-lg"
              style={{
                backgroundColor: '#f3f4f6',
                minHeight: '192px'
              }}
              onError={(e) => {
                console.error('图片加载失败:', displayUrl);
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
            {previewUrl && (
              <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                新图片
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  disabled={disabled}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  重新上传
                </Button>
                {previewUrl && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    disabled={disabled}
                  >
                    <X className="w-4 h-4 mr-1" />
                    取消
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            {loading ? (
              <div className="flex flex-col items-center space-y-2">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <p className="text-sm text-gray-600">上传中...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="p-3 bg-gray-100 rounded-full">
                  <Upload className="w-6 h-6 text-gray-600" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-900">
                    {placeholder}
                  </p>
                  <p className="text-xs text-gray-500">
                    支持 JPG、PNG、WebP、GIF 格式，最大 {maxSize}MB
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}; 
